import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import useSwr from 'swr';
import { AccountUtils } from '@bingads-webui-clientcenter/entity-utils';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  TopChangesFilterOption,
  fetchTopChanges,
  isInsightsMetric,
} from '@bingads-webui-campaign/overview-cards-common';


const accountsMode = 1; // 1 for expert mode, we don't expect to have smart mode account anymore

export function useTopChangesData({
  dateRange,
  filter,
  comparisonDateRange,
  selectedMenuItem,
  metric,
  jsdata,
  dateUtil,
  permissions,
  cardTypeId,
  constructOptions,
  dismissLastFluctuation,
  cacheManager,
  fetchSingleTile,
  perfMarkerUtils,
  getCardsConfig,
  campaignType,
  i18n,
}) {
  const {
    entitySet,
  } = constructOptions();


  const instrumentation = useContext(InstrumentationContext) as any;
  const uiActivity = useMemo(() => {
    if (instrumentation) {
      const scenario = instrumentation.createScenarioAndActivity({
        scenarioName: 'Fluent2OverviewPage',
        activityName: 'TopChanges',
      });
      if (scenario?.activity?.trace && scenario?.activity?.error) {
        return scenario.activity;
      }
    }

    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const fetch = useCallback(({
    dateRange, filter, selectedMenuItem, cardTypeId, metric,
  }) => {
    perfMarkerUtils?.willFetchData();
    return fetchTopChanges({
      entitySet,
      dateRange,
      filter,
      comparisonDateRange,
      selectedMenuItem,
      metric,
      jsdata,
      dateUtil,
      fetchSingleTile,
      isDevice: true,
      permissions,
      disableCache: dismissLastFluctuation,
      cacheManager,
      accountsMode,
      dfmCacheKey: cardTypeId,
      getCardsConfig,
      campaignType,
    }).then((data) => {
      perfMarkerUtils?.willRender();

      if (!data || (data.status !== null && data.status !== undefined)) {
        // If we have error status. Those are already logged in ajax stub.
        // Add a trace log.
        uiActivity.trace(`top-changes-card failed to load data - data:${JSON.stringify(data)}`);
        if (data?.readyState === 0) {
          uiActivity.error('top-changes-card failed to load data - readyState=0');
        }
        throw new Error(i18n.getString(_TL_('Failed to load data')));
      }

      let dataError = false;

      let gridData = _.map(data, (entity) => {
        if (!entity || !entity.PerformanceMetricsChange) {
          dataError = true;
        }
        if (entity.AvailableInsightTypeIds && entity.AvailableInsightTypeIds.length) {
          const fromScenario = entity.CampaignType ? 'CampaignsOverview' : 'AccountsOverView';
          uiActivity.trace(`top-changes-card Fluctuations Rendered. FromScenario: ${fromScenario}, FilterOption: ${selectedMenuItem}, Metric: ${metric}, TypeIds: ${entity.AvailableInsightTypeIds.toString()}, id: ${entity.Id}}.`);
        }
        if (entity?.AccountInactiveReasons) {
          return { ...entity, Status: AccountUtils.activityStatus(entity) };
        }
        return entity;
      });

      if (dataError) {
        uiActivity.error(`top-changes-card data error - data:${JSON.stringify(data)}`);
        if (data?.readyState === 0) {
          uiActivity.error('top-changes-card data error - readyState=0');
        }
        throw new Error(i18n.getString(_TL_('Failed to load data')));
      }

      if (selectedMenuItem === TopChangesFilterOption.TopDecliners) {
        gridData = _.filter(gridData, entity => entity.PerformanceMetricsChange[metric] < 0);
      } else if (selectedMenuItem === TopChangesFilterOption.TopGainers) {
        gridData = _.filter(gridData, entity => entity.PerformanceMetricsChange[metric] > 0);
      } else if (selectedMenuItem === TopChangesFilterOption.Insights) {
        gridData = isInsightsMetric(metric) ? _.filter(
          gridData,
          entity => !_.isEmpty(entity.AvailableInsightTypeIds)
        ) : [];
      }

      return gridData;
    }).catch((response) => {
      uiActivity.error(`top-changes-card catch error - response:${JSON.stringify(response)}`);
      if (response?.readyState === 0) {
        uiActivity.error('top-changes-card catch error - readyState=0');
      }
      throw new Error(i18n.getString(_TL_('Failed to load data')));
    });
  }, [perfMarkerUtils, entitySet, comparisonDateRange, jsdata, dateUtil, fetchSingleTile, permissions, dismissLastFluctuation, cacheManager, getCardsConfig, campaignType, uiActivity, i18n]);


  return useSwr({
    dateRange, filter, selectedMenuItem, cardTypeId, metric,
  }, fetch, {
    revalidateOnFocus: false,
  });
}
